import moment, { Moment } from 'moment';
import Log from '../models/Log';
import User from '../models/User';
import { GroupedActivityLog } from './groupActivityLogs';

export type ActivityCategory = 'all' | 'team' | 'email' | 'ci' | 'sentry' | 'quotes';
/** Filters for the personal "My activity" screen. */
export type MineActivityCategory = 'all' | 'comments' | 'status' | 'patches' | 'quotes';
export type ActivitySignal = 'high' | 'medium' | 'low';

function hasEmailIntakeSender(change?: Log['change']): boolean {
    const sender = change?.intake?.sender;
    if (!sender)
        return false;
    return !!(sender.email || sender.domain);
}

export type ActivityDaySection = {
    key: string;
    labelKey: 'day_today' | 'day_yesterday' | 'day_date';
    date: Moment;
    entries: GroupedActivityLog[];
};

const HIGH_SIGNAL_TYPES = new Set([
    Log.TYPE_CHANGE_CREATE,
    Log.TYPE_CHANGE_DEPLOYED,
    Log.TYPE_CHANGE_TEAM_REVIEW_FINISHED,
    Log.TYPE_CHANGE_CLIENT_REVIEW_FINISHED,
    Log.TYPE_CHANGE_DISCARDED,
    Log.TYPE_CHANGE_RESTORED,
    Log.TYPE_QUOTE_ACCEPTED,
    Log.TYPE_QUOTE_REFUSED,
    Log.TYPE_QUOTE_PAID,
    Log.TYPE_QUOTE_BILLED,
    Log.TYPE_QUOTE_ADVANCE_PAID,
    Log.TYPE_QUOTE_ADVANCE_BILLED,
]);

const MEDIUM_SIGNAL_TYPES = new Set([
    Log.TYPE_CHANGE_STARTED,
    Log.TYPE_CHANGE_ASSIGNED,
    Log.TYPE_CHANGE_UNASSIGNED,
    Log.TYPE_CHANGE_ESTIMATED,
    Log.TYPE_CHANGE_UNESTIMATED,
    Log.TYPE_CHANGE_PLANNED,
    Log.TYPE_CHANGE_UNPLANNED,
    Log.TYPE_CHANGE_SPRINT_ADDED,
    Log.TYPE_CHANGE_SPRINT_REMOVED,
    Log.TYPE_QUOTE_CREATED,
    Log.TYPE_QUOTE_SUBMITTED,
    Log.TYPE_COMMENT_CREATE,
    Log.TYPE_COMMENT_UPDATE,
    Log.TYPE_ATTACHMENT_ACCESSED,
]);

const LOW_SIGNAL_TYPES = new Set([
    Log.TYPE_CHANGE_PATCH,
    Log.TYPE_CHANGE_TEAM_REVIEW_STARTED,
    Log.TYPE_CHANGE_CLIENT_REVIEW_STARTED,
]);

const LOW_AI_TYPES = new Set([
    'ai:ensure_active_track',
    'ai:stop_active_track',
]);

function authorDisplayName(author?: User): string {
    if (!author)
        return '';
    return [author.firstname, author.lastname].filter(Boolean).join(' ');
}

function isBot(author?: User): boolean {
    return author?.account_type === User.ACCOUNT_TYPE_BOT;
}

/**
 * Classify a log into a filter category (team / email / ci / sentry / quotes).
 * Email: comment.source email_intake, or change:create with intake.sender (not Signal).
 * Sentry: AI audit types, integration author name, or change linked to a Sentry issue.
 * GitLab: bot author name or review/tests log types.
 */
export function classifyActivityLog(log: Log): Exclude<ActivityCategory, 'all'> {
    if (log.type?.startsWith('quote:'))
        return 'quotes';

    if (log.type?.startsWith('ai:sentry'))
        return 'sentry';

    const name = authorDisplayName(log.author);
    if (/sentry/i.test(name))
        return 'sentry';

    if (
        log.type === Log.TYPE_CHANGE_CREATE &&
        log.change?.issue?.source === 'sentry'
    )
        return 'sentry';

    if (
        log.type === Log.TYPE_ATTACHMENT_ACCESSED ||
        log.comment?.source === 'email_intake' ||
        (log.type === Log.TYPE_CHANGE_CREATE && hasEmailIntakeSender(log.change))
    )
        return 'email';

    if (
        (isBot(log.author) && /gitlab/i.test(name)) ||
        log.type === Log.TYPE_CHANGE_TEAM_REVIEW_STARTED ||
        log.type === Log.TYPE_CHANGE_TEAM_REVIEW_FINISHED ||
        log.type === Log.TYPE_CHANGE_CLIENT_REVIEW_STARTED ||
        log.type === Log.TYPE_CHANGE_CLIENT_REVIEW_FINISHED
    )
        return 'ci';

    return 'team';
}

/**
 * Rank a log for visual hierarchy in the activity digest.
 */
export function activitySignal(log: Log): ActivitySignal {
    if (LOW_AI_TYPES.has(log.type) || LOW_SIGNAL_TYPES.has(log.type))
        return 'low';
    if (HIGH_SIGNAL_TYPES.has(log.type))
        return 'high';
    if (MEDIUM_SIGNAL_TYPES.has(log.type) || log.type?.startsWith('ai:'))
        return 'medium';
    return 'medium';
}

const SIGNAL_RANK: Record<ActivitySignal, number> = {
    high: 3,
    medium: 2,
    low: 1,
};

/**
 * Rank a grouped entry (including bursts) for visual hierarchy.
 */
export function activityEntrySignal(entry: GroupedActivityLog): ActivitySignal {
    if (!entry.members?.length)
        return activitySignal(entry.log);

    return entry.members.reduce<ActivitySignal>((best, member) => {
        const signal = activitySignal(member.log);
        return SIGNAL_RANK[signal] > SIGNAL_RANK[best] ? signal : best;
    }, 'low');
}

export function matchesActivityCategory(log: Log, category: ActivityCategory): boolean {
    if (category === 'all')
        return true;
    return classifyActivityLog(log) === category;
}

/**
 * Classify a log for the personal activity screen (comments / status / patches / quotes).
 */
export function classifyMineActivityLog(log: Log): Exclude<MineActivityCategory, 'all'> {
    if (log.type?.startsWith('comment:'))
        return 'comments';
    if (log.type?.startsWith('quote:'))
        return 'quotes';
    if (log.type === Log.TYPE_CHANGE_PATCH)
        return 'patches';
    return 'status';
}

export function matchesMineActivityCategory(log: Log, category: MineActivityCategory): boolean {
    if (category === 'all')
        return true;
    return classifyMineActivityLog(log) === category;
}

/**
 * Group already-sorted (newest-first) activity entries into calendar-day sections.
 */
export function groupByActivityDay(entries: GroupedActivityLog[], now: Moment = moment()): ActivityDaySection[] {
    const sections: ActivityDaySection[] = [];
    const today = now.clone().startOf('day');
    const yesterday = today.clone().subtract(1, 'day');

    entries.forEach((entry) => {
        const day = entry.createdAt.clone().startOf('day');
        const key = day.format('YYYY-MM-DD');
        let section = sections.find((s) => s.key === key);

        if (!section) {
            let labelKey: ActivityDaySection['labelKey'] = 'day_date';
            if (day.isSame(today, 'day'))
                labelKey = 'day_today';
            else if (day.isSame(yesterday, 'day'))
                labelKey = 'day_yesterday';

            section = { key, labelKey, date: day, entries: [] };
            sections.push(section);
        }

        section.entries.push(entry);
    });

    return sections;
}
